import './Jobs.css';
import { Box, Grid, Typography, Drawer, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import Filter, {
  CheckSelect,
  DropdownSelect,
} from '../../components/Filter/Filter';
import JobCard from '../../components/JobCard/JobCard';
import { useEffect, useReducer, useState } from 'react';
import HttpHelper from '../../utils/HttpHelper';
import { useJobContext } from '../../JobContext';
import { useLoading } from '../../contexts/LoadingContext';

export interface Job {
  [key: string]: string | boolean | number;
  applicationDeadline: string;
  city: string;
  country: string;
  currentlyAvailable: boolean;
  datePosted: string;
  id: number;
  jobCategory: number;
  jobType: number;
  province: string;
  published: boolean;
  remote: boolean;
  salary: number;
  title: string;
}

const Jobs: React.FC = () => {
  const [jobList, setJobList] = useState<Job[]>([]);
  const [originalJobList, setOriginalJobList] = useState<Job[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);

  // Use the global loading context instead of local state
  const { showLoading, hideLoading } = useLoading();

  // const [displayedJobs, dispatch] = useReducer(produce(JobsReducer), []);
  const [dropdownSelectData, setDropdownSelectData] = useState<
    DropdownSelect[]
  >([]);
  const [filterState, setFilterState] = useState<{
    [key: string]: number | string | boolean | null;
  }>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const updateJobs = async (url: string): Promise<void> => {
    try {
      showLoading('Loading jobs...');
      const response = await HttpHelper.getPublic(url);

      if (response?.errorMessage) {
        alert(response.errorMessage);
        return;
      }

      if (url === 'jobs/') setOriginalJobList(response?.jobs || []);
      setJobList(response?.jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      alert('Failed to load jobs. Please try again.');
      setJobList([]);
    } finally {
      setHasLoadedOnce(true);
      hideLoading();
    }
  };

  useEffect(() => {
    console.log(filterState);
    let queryUrl = 'jobs/';
    let isFirstArg = true;
    Object.keys(filterState).forEach(k => {
      if (filterState[k]) {
        if (isFirstArg) {
          queryUrl += `?${k}=${filterState[k]}&`;
          isFirstArg = false;
        } else {
          queryUrl += `${k}=${filterState[k]}&`;
        }
      }
    });
    updateJobs(queryUrl);
  }, [filterState]);

  useEffect(() => {
    const jobTypeOptions = originalJobList
      .map(d => {
        return {
          value: d.jobType,
          text: inverseJobType[d.jobType],
        };
      })
      .filter(
        (obj, index, self) =>
          index ===
          self.findIndex(t => t.value === obj.value && t.text === obj.text),
      );
    const jobCategoryOptions = originalJobList
      .map(d => {
        return {
          value: d.jobCategory,
          text: inverseJobCategory[d.jobCategory],
        };
      })
      .filter(
        (obj, index, self) =>
          index ===
          self.findIndex(t => t.value === obj.value && t.text === obj.text),
      );
    let dropdownSelectData: DropdownSelect[] = [
      {
        label: 'Job Type',
        labelId: 'job-type-label',
        categoryName: 'jobType',
        options: jobTypeOptions,
      },
      {
        label: 'Job Category',
        labelId: 'job-category',
        categoryName: 'jobCategory',
        options: jobCategoryOptions,
      },
    ];
    setDropdownSelectData(dropdownSelectData);
  }, [originalJobList]);

  const { jobTypeMapping, jobCategoryMapping } = useJobContext();

  const inverseJobType = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [value, key]),
  );

  const inverseJobCategory = Object.fromEntries(
    Object.entries(jobCategoryMapping).map(([key, value]) => [value, key]),
  );

  const SelectBoxesData: CheckSelect[] = [
    // {
    //   label: 'Currently Available',
    //   categoryName: 'currentlyAvailable',
    // },
    // {
    //   label: 'Published Only',
    //   categoryName: 'published',
    // },
    {
      label: 'Remote Only',
      categoryName: 'remote',
    },
  ];

  // Don't show "no jobs" message until we've actually tried to load jobs
  if (!hasLoadedOnce) {
    return null; // or return <></> - renders nothing while loading
  }

  if (originalJobList.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Typography variant="h5">No published jobs at the moment.</Typography>
      </Box>
    );
  }

  const filterComponent = (
    <Filter
      CheckSelectData={SelectBoxesData}
      DropdownSelectData={dropdownSelectData}
      updateFilterState={setFilterState}
      useFixedPosition={false}
    />
  );

  return (
    <Box
      sx={{
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: { xs: '16px', lg: '24px' },
      }}
    >
      {/* Mobile Filter Button */}
      {isMobile && (
        <Box sx={{ maxWidth: '950px', margin: '0 auto', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setMobileFilterOpen(true)}
            fullWidth
          >
            Filters
          </Button>
        </Box>
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: '80%',
            maxWidth: '300px',
            padding: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setMobileFilterOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {filterComponent}
      </Drawer>

      <Grid container spacing={2}>
        {/* Desktop Filter Sidebar */}
        {!isMobile && (
          <Grid item lg={2.5} className="filterContainer">
            {filterComponent}
          </Grid>
        )}

        {/* Job List */}
        <Grid item xs={12} lg={9.5} className="jobList">
          <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
            {jobList.map((job, i) => (
              <JobCard
                key={i}
                jobTitle={job.title}
                location={`${job ? job.city : ''}, ${job ? job.province : ''}, ${job ? job.country : ''
                  }`}
                employmentType={inverseJobType[job.jobType]}
                jobId={job.id}
                remote={job.remote}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Jobs;
